import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import pt from '../../locales/pt';
import {
  defaultPreferences,
  hasValidConsent,
  writeCookieConsent,
} from '../../utils/cookieConsent';

/**
 * RGPD / ePrivacy cookie consent banner (first visit).
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(() => !hasValidConsent());
  const [customizing, setCustomizing] = useState(false);
  const [prefs, setPrefs] = useState(defaultPreferences);

  const save = (preferences) => {
    writeCookieConsent({
      decidedAt: new Date().toISOString(),
      preferences,
    });
    setVisible(false);
  };

  const acceptAll = () => {
    save({ necessary: true, analytics: true, marketing: true });
  };

  const rejectOptional = () => {
    save(defaultPreferences());
  };

  const saveCustom = () => {
    save({ ...prefs, necessary: true });
    setCustomizing(false);
  };

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          role="dialog"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-card/95 p-4 shadow-lg backdrop-blur sm:p-6"
        >
          <div className="mx-auto max-w-3xl">
            <h2 id="cookie-consent-title" className="text-lg font-semibold text-foreground">
              {pt.cookies.banner.title}
            </h2>
            <p id="cookie-consent-desc" className="mt-2 text-sm text-muted-foreground">
              {pt.cookies.banner.description}{' '}
              <Link to="/privacy" className="font-medium text-sage-700 underline">
                {pt.cookies.banner.privacyLink}
              </Link>
            </p>

            {customizing ? (
              <div className="mt-4 space-y-3 rounded-lg border border-border bg-background p-4">
                <p className="text-sm font-medium text-foreground">{pt.cookies.banner.necessaryTitle}</p>
                <p className="text-xs text-muted-foreground">{pt.cookies.banner.necessaryDesc}</p>
                <label className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Checkbox checked disabled />
                  <span>{pt.cookies.banner.necessaryLabel}</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Checkbox
                    checked={prefs.analytics}
                    onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: Boolean(v) }))}
                  />
                  <span>
                    <span className="font-medium">{pt.cookies.banner.analyticsTitle}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {pt.cookies.banner.analyticsDesc}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Checkbox
                    checked={prefs.marketing}
                    onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: Boolean(v) }))}
                  />
                  <span>
                    <span className="font-medium">{pt.cookies.banner.marketingTitle}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {pt.cookies.banner.marketingDesc}
                    </span>
                  </span>
                </label>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button type="button" onClick={saveCustom}>
                    {pt.cookies.banner.savePreferences}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setCustomizing(false)}>
                    {pt.cookies.banner.back}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button type="button" onClick={acceptAll}>
                  {pt.cookies.banner.acceptAll}
                </Button>
                <Button type="button" variant="outline" onClick={rejectOptional}>
                  {pt.cookies.banner.rejectOptional}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setCustomizing(true)}>
                  {pt.cookies.banner.customize}
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
